/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useState } from 'react';
import { User } from 'firebase/auth';
import { SUBSCRIPTION_OPTIONS, SubscriptionTier, stripeService } from '../services/StripeService';
import './auth.css';

// CSS styles for modal
const modalOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  right: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
  backgroundColor: 'white',
  padding: '5px 20px 5px',
  borderRadius: '10px',
  width: '400px',
  maxHeight: '90%',
  overflowY: 'auto',
  position: 'relative',
  textShadow: 'none',
  color: 'black',
  fontFamily: 'arial',
  textAlign: 'left',
  fontSize: 'small'
};

const closeButtonStyle: React.CSSProperties = {
  fontSize: 'x-small',
  height: '16px',
  borderRadius: '5px',
  background: 'lightgrey',
  padding: '2px',
  width: '16px',
  marginRight: '-14px',
  cursor: 'pointer'
};

const buttonStyle: React.CSSProperties = {
  fontSize: 'small',
  height: '24px',
  width: '180px',
  cursor: 'pointer'
};

const subscriptionCardStyle: React.CSSProperties = {
  border: '1px solid #ddd',
  borderRadius: '8px',
  padding: '15px',
  margin: '10px 0',
  cursor: 'pointer',
  transition: 'all 0.3s ease'
};

const selectedCardStyle: React.CSSProperties = {
  ...subscriptionCardStyle,
  border: '2px solid #4285f4',
  backgroundColor: '#f0f7ff'
};

interface SubscriptionModalProps {
  user: User;
  onClose: () => void;
}

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({ user, onClose }) => {
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const handleSubscribe = async () => {
    if (!selectedTier) {
      setError('Please select a subscription plan');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('Creating checkout session for tier:', selectedTier);
      const sessionId = await stripeService.createCheckoutSession(user, selectedTier);
      
      if (!sessionId) {
        throw new Error('Failed to create checkout session');
      }
      
      console.log('Redirecting to checkout with sessionId:', sessionId);
      await stripeService.redirectToCheckout(sessionId);
    } catch (error: any) {
      console.error('Error during subscription process:', error);
      // Show a more specific error message if available
      const errorMessage = error?.message || 'An error occurred during the subscription process. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={modalOverlayStyle}>
      <div style={modalStyle}>
        <div style={{ textAlign: 'right' }}>
          <button style={closeButtonStyle} onClick={onClose}>X</button>
        </div>
        
        <div style={{ textAlign: 'center' }}>
          <h2>Choose Your Subscription</h2>
          <p>Select a plan that works for you</p>
          
          <table>
            <tbody>
              <tr>
                {SUBSCRIPTION_OPTIONS.map((option) => (
                  <td key={option.id}>
                    <div style={selectedTier === option.id ? selectedCardStyle : subscriptionCardStyle}
                         onClick={() => setSelectedTier(option.id)}>
                      <h3>{option.name}</h3>
                      <h2>{option.price}</h2>
                      <p>{option.description}</p>
                    </div>
                  </td>
                ))}

              </tr>
            </tbody>
          </table>
          
          {error && <p style={{ color: 'red' }}>{error}</p>}
          
          <button 
            style={buttonStyle} 
            onClick={handleSubscribe} 
            disabled={!selectedTier || loading}
          >
            {loading ? 'Processing...' : 'Subscribe Now'}
          </button>
          
          <p style={{ fontSize: 'x-small', marginTop: '20px' }}>
            Payment securely processed by Stripe. Your subscription will automatically renew annually.
            You can cancel anytime from your account settings.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal;